import React from 'react'
import { useDispatch } from 'react-redux'
import { Transition, TransitionGroup } from 'react-transitioning'
import type { TransitionPhase, TransitionState } from 'react-transitioning'
import { Icon } from '@mobi/component-library/Legacy/Icon'
import { useBodyScrollLock } from '@mobi/utils/hooks/useBodyScrollLock'
import { useAppSelector } from '@core/Store/hooks'
import {
  ANIMATION_DURATION_MS,
  ModalAnimationContainerStyled as AnimationContainer,
  ModalBackgroundStyled as Background,
  ModalCloseButtonStyled as CloseButton,
  ModalContentContainerStyled as ContentContainer,
  ModalPositionStyled as Position,
  ModalScreenCoverStyled as ScreenCover,
} from './Modal.styles'
import { closeModal } from './store'
import type { ModalItem } from './types'

/** Handles rendering of open modal instances  */
/** @deprecated - Use Overlays/Modal/Modal.tsx instead */
export const ModalContainer: React.FC = () => {
  const items = useAppSelector(rootState => rootState.modal.items)

  return (
    <TransitionGroup>
      {items.map(item => (
        <Transition key={item.id} in duration={ANIMATION_DURATION_MS}>
          {(transitionState, activePhase) => (
            <Modal transitionState={transitionState} activePhase={activePhase} item={item} />
          )}
        </Transition>
      ))}
    </TransitionGroup>
  )
}

// Single modal instance
const Modal = ({
  transitionState,
  activePhase,
  item: {
    id,
    modalComponent,
    modalComponentProps,
    color,
    exitAnimation,
    isDismissable = true,
    onCloseCallback,
    position,
    cornerStyle,
  },
}: {
  transitionState: TransitionState
  activePhase: TransitionPhase
  item: ModalItem
}) => {
  const modalContainerRef = useBodyScrollLock()
  const dispatch = useDispatch()

  const handleClose = () => {
    if (!transitionState.enterActive && !transitionState.appearActive && isDismissable) {
      dispatch(closeModal({ id }))
    }
  }

  React.useEffect(() => {
    return onCloseCallback
  }, [onCloseCallback])

  return (
    <ScreenCover data-testid={`modal-item-${id}`}>
      <Position position={position}>
        <AnimationContainer
          id={id}
          transitionState={transitionState}
          activePhase={activePhase}
          ref={modalContainerRef}
          exitAnimation={exitAnimation}
        >
          <ContentContainer cornerStyle={cornerStyle}>
            {isDismissable && (
              <CloseButton onClick={handleClose} color={color} data-testid='modal-close-button'>
                <Icon type='cross' size='inherit' />
              </CloseButton>
            )}

            <Content content={modalComponent} contentProps={modalComponentProps} />
          </ContentContainer>
        </AnimationContainer>
      </Position>

      <Background
        onClick={isDismissable ? handleClose : undefined}
        transitionState={transitionState}
        activePhase={activePhase}
      />
    </ScreenCover>
  )
}

// React.memo w/ areEqual = true ensures no further renders of modal content after mount
const Content = React.memo(
  ({
    content,
    contentProps = {},
  }: {
    content: React.ElementType
    contentProps?: Record<string, unknown>
  }) => {
    const Content = content
    return <Content {...contentProps} />
  },
  () => true
)

Content.displayName = 'Modal.Content'
